import type { Bundle } from '@medplum/fhirtypes';
import { useSubscription } from '@medplum/react';
import type { UseSubscriptionOptions } from '@medplum/react';
import { isSubscriptionLimitError } from '@bariendo/lib/error';
import { alert } from './alert';

let hasShownLimitAlert = false;

/**
 * Show an alert when we detect that the user has likely hit the subscription
 * limit (usually due to too many open tabs).
 */
export function showSubscriptionLimitNotification(): void {
  if (hasShownLimitAlert) {
    return;
  }

  hasShownLimitAlert = true;

  alert.warning({
    title: 'It looks like you have several Orbit windows open.',
    message:
      "To keep everything running smoothly, please close any tabs you're not using. If something isn't loading, try refreshing this page after closing extra tabs.",
    options: {
      autoClose: false,
    },
  });
}

/**
 * React hook wrapper around `useSubscription` that adds centralized handling
 * for subscription-limit errors while delegating all other behavior to
 * Medplum's hook.
 */
export function useSubscriptionWithLimitHandling(
  criteria: string | undefined,
  callback: (bundle: Bundle) => void,
  options?: UseSubscriptionOptions
): void {
  const wrappedOptions: UseSubscriptionOptions = {
    ...options,
    onError: (err: Error) => {
      if (isSubscriptionLimitError(err)) {
        showSubscriptionLimitNotification();
      }

      // Delegate to caller-provided onError (if any) so existing
      // behavior is preserved.
      options?.onError?.(err);
    },
  };

  useSubscription(criteria, callback, wrappedOptions);
}
